import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Star,
  Search,
  SlidersHorizontal,
  X,
  ChevronDown,
  ChevronRight,
  LayoutGrid,
} from 'lucide-react';
import { priceLabel } from '../mock';
import { useCatalog } from '../hooks/useCatalog';

const API = process.env.REACT_APP_BACKEND_URL;

const sortOptions = [
  { id: 'recommended', label: 'Direkomendasikan' },
  { id: 'name-asc', label: 'Nama A-Z' },
  { id: 'name-desc', label: 'Nama Z-A' },
  { id: 'rating', label: 'Rating tertinggi' },
];

// Compute counts per parent category & subcategory
const useCounts = (products) =>
  useMemo(() => {
    const parent = {};
    const sub = {};
    products.forEach((p) => {
      parent[p.category] = (parent[p.category] || 0) + 1;
      if (p.subcategory) sub[p.subcategory] = (sub[p.subcategory] || 0) + 1;
    });
    return { parent, sub, total: products.length };
  }, [products]);

export default function Products() {
  const { categoryTree, products, findSubcategory } = useCatalog();
  const [params, setParams] = useSearchParams();

  // URL search params are the single source of truth so that
  // mega-menu / footer navigation always updates the view instantly
  const category = params.get('category') || 'all';
  const subcategory = params.get('sub') || null;
  const query = params.get('q') || '';

  const [sort, setSort] = useState('recommended');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [expanded, setExpanded] = useState(() =>
    category !== 'all' ? { [category]: true } : {}
  );

  const counts = useCounts(products);

  const setFilters = ({
    category: c = category,
    subcategory: s = subcategory,
    query: q = query,
  }) => {
    const next = new URLSearchParams();
    if (c !== 'all') next.set('category', c);
    if (s) next.set('sub', s);
    if (q.trim()) next.set('q', q.trim());
    setParams(next, { replace: true });
  };

  // Auto-expand the active category when navigated via URL (mega-menu click)
  useEffect(() => {
    if (category !== 'all') {
      setExpanded((e) => (e[category] ? e : { ...e, [category]: true }));
    }
  }, [category]);

  const setQuery = (q) => setFilters({ query: q });

  // Reset subcategory when parent changes
  const selectCategory = (slug) => {
    setFilters({ category: slug, subcategory: null });
  };

  const selectSubcategory = (parentSlug, subSlug) => {
    setFilters({ category: parentSlug, subcategory: subSlug });
    setMobileFilterOpen(false);
  };

  const toggleExpand = (slug) =>
    setExpanded((e) => ({ ...e, [slug]: !e[slug] }));

  const filtered = useMemo(() => {
    let list = [...products];
    if (category !== 'all') list = list.filter((p) => p.category === category);
    if (subcategory) list = list.filter((p) => p.subcategory === subcategory);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.sku.toLowerCase().includes(q) ||
          p.series.toLowerCase().includes(q) ||
          p.capacity.toLowerCase().includes(q)
      );
    }
    if (sort === 'name-asc') list.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'name-desc') list.sort((a, b) => b.name.localeCompare(a.name));
    if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [category, subcategory, query, sort, products]);

  const activeParent = categoryTree.find((c) => c.slug === category);
  const activeSub = subcategory
    ? findSubcategory(category, subcategory)
    : null;

  // Group when browsing "Semua" — show sections by parent category
  const grouped = useMemo(() => {
    if (category !== 'all') return null;
    const q = query.trim().toLowerCase();
    return categoryTree.map((parent) => {
      let items = products.filter((p) => p.category === parent.slug);
      if (q)
        items = items.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.sku.toLowerCase().includes(q) ||
            p.series.toLowerCase().includes(q) ||
            p.capacity.toLowerCase().includes(q)
        );
      return { parent, items };
    });
  }, [category, query, categoryTree, products]);

  // Sidebar tree rendering
  const SidebarTree = ({ onNavigate }) => (
    <nav data-testid="products-sidebar" className="text-sm">
      <button
        onClick={() => {
          selectCategory('all');
          onNavigate?.();
        }}
        data-testid="category-filter-all"
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl mb-2 font-semibold transition-colors ${
          category === 'all'
            ? 'bg-neutral-900 text-white'
            : 'bg-neutral-50 text-neutral-800 hover:bg-neutral-100'
        }`}
      >
        <span className="flex items-center gap-2">
          <LayoutGrid className="w-4 h-4" /> Semua Produk
        </span>
        <span className="text-xs opacity-80">{counts.total}</span>
      </button>

      <ul className="space-y-1">
        {categoryTree
          .filter((c) => (counts.parent[c.slug] || 0) > 0 || c.alwaysVisible)
          .map((c) => {
          const isActive = category === c.slug && !subcategory;
          const isExpanded = expanded[c.slug];
          const visibleSubs = c.subcategories.filter(
            (s) => (counts.sub[s.slug] || 0) > 0 || s.alwaysVisible
          );
          const hasSubs = visibleSubs.length > 0;
          return (
            <li key={c.slug}>
              <div
                className={`flex items-stretch rounded-xl overflow-hidden ${
                  isActive ? 'bg-red-50 ring-1 ring-red-200' : 'hover:bg-neutral-50'
                }`}
              >
                <button
                  onClick={() => {
                    selectCategory(c.slug);
                    if (!hasSubs) onNavigate?.();
                  }}
                  data-testid={`category-filter-${c.slug}`}
                  className={`flex-1 text-left px-4 py-2.5 font-semibold ${
                    isActive ? 'text-px-red' : 'text-neutral-800'
                  }`}
                >
                  {c.title}
                  <span className="ml-2 text-xs font-normal text-neutral-500">
                    ({counts.parent[c.slug] || 0})
                  </span>
                </button>
                {hasSubs && (
                  <button
                    onClick={() => toggleExpand(c.slug)}
                    aria-label={`toggle ${c.title}`}
                    className="px-3 text-neutral-500 hover:text-neutral-800"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>

              {hasSubs && isExpanded && (
                <ul className="ml-3 mt-1 mb-2 border-l border-neutral-200 pl-3 space-y-0.5">
                  {visibleSubs.map((s) => {
                    const activeSubItem =
                      category === c.slug && subcategory === s.slug;
                    return (
                      <li key={s.slug}>
                        <button
                          onClick={() => {
                            selectSubcategory(c.slug, s.slug);
                            onNavigate?.();
                          }}
                          data-testid={`subcategory-filter-${s.slug}`}
                          className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-lg text-sm ${
                            activeSubItem
                              ? 'bg-neutral-900 text-white font-semibold'
                              : 'text-neutral-700 hover:bg-neutral-100'
                          }`}
                        >
                          <span className="truncate">{s.title}</span>
                          <span className="text-[11px] opacity-80 ml-2">
                            {counts.sub[s.slug] || 0}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );

  const ProductCard = ({ p }) => (
    <Link
      key={p.id}
      to={`/products/${p.id}`}
      data-testid={`product-card-${p.id}`}
      className="group relative bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:shadow-lg hover:-translate-y-1 transition-all flex flex-col"
    >
      {p.badge && (
        <span className="absolute top-4 left-4 z-10 bg-px-red text-white text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-full">
          {p.badge}
        </span>
      )}
      <div className="aspect-square bg-neutral-50 overflow-hidden flex items-center justify-center">
        <img
          src={p.image?.startsWith('/api/') ? `${API}${p.image}` : p.image}
          alt={p.name}
          className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <p className="text-[10px] tracking-[0.2em] text-neutral-500 font-semibold mb-1">
          {p.sku}
        </p>
        <h3 className="font-display text-base font-bold text-neutral-900 mb-1 line-clamp-2 min-h-[2.6em]">
          {p.name}
        </h3>
        <p className="text-xs text-neutral-500 mb-4 line-clamp-1">
          {p.capacity}
        </p>
        <div className="mt-auto flex items-center justify-between">
          <span className="text-sm font-bold text-px-red">
            {priceLabel(p.price)}
          </span>
          <span className="flex items-center gap-1 text-xs text-neutral-600">
            <Star className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />
            {p.rating}
          </span>
        </div>
      </div>
    </Link>
  );

  const heroImage =
    activeParent?.image ||
    'https://images.pexels.com/photos/17489163/pexels-photo-17489163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  return (
    <div data-testid="page-products" className="pt-[68px] md:pt-[100px] bg-white">
      {/* Header */}
      <section className="relative h-[40vh] min-h-[300px] bg-neutral-950 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 h-full flex flex-col justify-center text-white">
          <nav className="text-xs text-neutral-300 flex items-center gap-2 mb-4">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span>/</span>
            <span className={category === 'all' ? 'text-white' : ''}>Produk</span>
            {activeParent && (
              <>
                <span>/</span>
                <span className={!activeSub ? 'text-white' : ''}>
                  {activeParent.title}
                </span>
              </>
            )}
            {activeSub && (
              <>
                <span>/</span>
                <span className="text-white">{activeSub.title}</span>
              </>
            )}
          </nav>
          <p className="text-xs tracking-[0.25em] text-red-400 font-semibold mb-3">
            {activeParent
              ? activeParent.subtitle.toUpperCase()
              : 'KATALOG PRODUK'}
          </p>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-3">
            {activeSub
              ? activeSub.title
              : activeParent
              ? activeParent.title
              : 'Semua Produk PowerX'}
          </h1>
          <p className="text-neutral-300 max-w-xl">
            {activeSub
              ? activeSub.short
              : activeParent
              ? activeParent.description
              : 'UPS, baterai, inverter, dan panel surya untuk semua skala — dari rumah pribadi hingga pusat data.'}
          </p>
        </div>
      </section>

      {/* Sticky filter bar (search + sort + mobile filter toggle) */}
      <section className="border-b border-neutral-200 sticky top-[68px] md:top-[100px] z-30 bg-white/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-3 flex items-center gap-3">
          <button
            onClick={() => setMobileFilterOpen(true)}
            className="lg:hidden inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neutral-300 text-sm font-medium"
            data-testid="filter-toggle-btn"
          >
            <SlidersHorizontal className="w-4 h-4" /> Kategori
          </button>

          <div className="flex items-center bg-neutral-100 rounded-full px-3 py-2 flex-1 max-w-md focus-within:ring-2 focus-within:ring-red-200">
            <Search className="w-4 h-4 text-neutral-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama, SKU, atau kapasitas..."
              className="bg-transparent outline-none text-sm ml-2 w-full"
              data-testid="products-search-input"
            />
            {query && (
              <button onClick={() => setQuery('')} aria-label="clear">
                <X className="w-4 h-4 text-neutral-500" />
              </button>
            )}
          </div>

          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="ml-auto bg-white border border-neutral-300 rounded-full px-4 py-2 text-sm outline-none cursor-pointer hidden md:block"
            data-testid="products-sort-select"
          >
            {sortOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <div className="grid lg:grid-cols-[280px_1fr] gap-10">
          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-[170px]">
              <p className="text-[11px] tracking-[0.25em] text-neutral-500 font-semibold mb-4">
                KATEGORI
              </p>
              <SidebarTree />
            </div>
          </aside>

          {/* Main */}
          <div>
            <p
              className="text-sm text-neutral-500 mb-6"
              data-testid="products-count"
            >
              Menampilkan <strong className="text-neutral-900">{filtered.length}</strong> produk
              {activeParent && (
                <>
                  {' '}dalam{' '}
                  <span className="font-semibold text-neutral-900">
                    {activeParent.title}
                  </span>
                </>
              )}
              {activeSub && (
                <>
                  {' '}›{' '}
                  <span className="font-semibold text-neutral-900">
                    {activeSub.title}
                  </span>
                </>
              )}
            </p>

            {category === 'all' && !query.trim() ? (
              // Group view: all categories, each with its own section
              <div className="space-y-14">
                {grouped.map(({ parent, items }) =>
                  items.length === 0 ? null : (
                    <div key={parent.slug}>
                      <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
                        <div>
                          <p className="text-[11px] tracking-[0.25em] text-px-red font-semibold mb-1">
                            {parent.subtitle.toUpperCase()}
                          </p>
                          <h2 className="font-display text-2xl md:text-3xl font-bold text-neutral-900">
                            {parent.title}
                          </h2>
                        </div>
                        <button
                          onClick={() => selectCategory(parent.slug)}
                          className="text-sm font-semibold text-neutral-900 border-b-2 border-px-red pb-0.5 hover:text-px-red"
                          data-testid={`view-all-${parent.slug}`}
                        >
                          Lihat semua {items.length} →
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
                        {items.slice(0, 4).map((p) => (
                          <ProductCard key={p.id} p={p} />
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-24" data-testid="products-empty">
                <p className="text-neutral-500 mb-4">
                  Tidak ada produk yang cocok dengan pencarian Anda.
                </p>
                <button
                  onClick={() => {
                    setQuery('');
                    selectCategory('all');
                  }}
                  className="text-px-red font-semibold underline"
                >
                  Reset filter
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map((p) => (
                  <ProductCard key={p.id} p={p} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile Filter Drawer */}
      {mobileFilterOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/60" onClick={() => setMobileFilterOpen(false)}>
          <div
            className="absolute inset-y-0 left-0 w-[85%] max-w-sm bg-white p-5 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <p className="font-display text-xl font-bold">Kategori</p>
              <button
                onClick={() => setMobileFilterOpen(false)}
                aria-label="close"
                className="p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <SidebarTree onNavigate={() => setMobileFilterOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
